
import './index.less';
import React, { createRef } from 'react';
import { FileInfo } from '@/common/services/FileCenter';
import { EditViewStatus, IFileCenterContext, Watermark } from '@/common/components/FileCenterProvider';
import { getImageRatio, getRotatedElementCorners, getRotatedRectCorners, getString, getTextWH, mod, throttle } from '@/common/utils/utils';
import type { IPoint, Rect } from '@/common/utils/utils';
import AnchorDot, { AnchorDotType, getAnchorDotInfo } from '@/common/components/AnchorDot/AnchorDot';
import RotateAnchorDot from '@/common/components/AnchorDot/RotateAnchorDot';
import { afterDraw, beforeDraw, DrawContext, drawWatermark } from '@/common/utils/WatermarkCanvas';
import functionSwitchConfig from '@/functionSwitchConfig';
import { getPositionLeftTop } from '@/common/utils/positionUtil';
import userAgentUtils from '@/common/utils/userAgent';
import { PositionType } from "@/common/constants/interfaces";

interface IProps extends IFileCenterContext {
    selectedItem: FileInfo | undefined,
    onTextEditing?: () => void,
}

interface IState {
    left: number;
    top: number;
    width: number;
    height: number;
    cursorStyle: string | undefined;
    refWaterMarksMap: Map<string, React.RefObject<HTMLDivElement>>;
}

class ImageEditView extends React.PureComponent<IProps, IState> {
    private readonly AnchorDotSize = 8;

    private ratio = 1;
    private startMoveFlag = false;
    private startSetSizeProps: any = null;
    private startRotateProps: any = null;

    private xOffsetWaterMark = 0;
    private yOffsetWaterMark = 0;
    private waterMarkContainerRect: Rect | undefined = undefined
    private refWaterMarkContainer = createRef<HTMLDivElement>();
    private refContainer = createRef<HTMLDivElement>();
    private refCanvas = createRef<HTMLCanvasElement>();
    private ctx: CanvasRenderingContext2D | null | undefined = null;

    // 用于判断是否在水印上mousedown
    private mouseDownWatermarkFlag = false;

    constructor(props: IProps) {
        super(props);
        this.state = {
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            cursorStyle: undefined,
            refWaterMarksMap: new Map<string, React.RefObject<HTMLDivElement>>(),
        };
    }

    static getNewRefWaterMarksMap(props: IProps) {
        const { watermarks } = props
        const refMap = new Map<string, React.RefObject<HTMLDivElement>>();
        watermarks.forEach((watermark) => {
            refMap.set(watermark.id, createRef())
        })
        return { refWaterMarksMap: refMap };
    }

    /**
     *   当props.watermarks发生变化时，refWaterMarksMap也要相应变化并且要在render之前更新，
     *   所以只能放在getDerivedStateFromProps方法中，由于getDerivedStateFromProps是静态方法，
     *   所以不能直接访问this.refWaterMarksMap，所以要把refWaterMarksMap放入state中
     */

    static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> | null {
        const { refWaterMarksMap } = prevState
        const { watermarks } = nextProps
        if (watermarks.length === refWaterMarksMap.size) { // 如果水印没有添加和删除，则不需要更新refWaterMarksMap
            for (const watermark of watermarks) {
                if (!refWaterMarksMap.has(watermark.id)) {
                    return ImageEditView.getNewRefWaterMarksMap(nextProps)
                }
            }
            return null
        }
        return ImageEditView.getNewRefWaterMarksMap(nextProps)
    }

    componentDidMount(): void {

        this.ctx = this.refCanvas.current?.getContext('2d');

        window.addEventListener('resize', this.updateSize)
        if (userAgentUtils.isMobile) {
            window.addEventListener('touchmove', this.handleMouseMove)
            window.addEventListener('touchend', this.handleMouseUp)
        } else {
            window.addEventListener('mousemove', this.handleMouseMove)
            window.addEventListener('mouseup', this.handleMouseUp)
        }

        this.updateSize()
    }

    componentWillUnmount(): void {
        if (userAgentUtils.isMobile) {
            window.removeEventListener('touchmove', this.handleMouseMove)
            window.removeEventListener('touchend', this.handleMouseUp)
        } else {
            window.removeEventListener('mousemove', this.handleMouseMove)
            window.removeEventListener('mouseup', this.handleMouseUp)
        }

        window.removeEventListener('resize', this.updateSize)
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
        if (prevProps.selectedItem !== this.props.selectedItem) {
            this.updateSize()
        }

        const drawContext = this.getDrawContext()
        beforeDraw(drawContext)
        this.props.watermarks.forEach((watermark: Watermark) => { drawWatermark(watermark, drawContext) })
        afterDraw(drawContext)
    }

    private getWaterMarkStr(watermark: Watermark) {
        const { selectedItem, } = this.props
        if (!selectedItem) return ''
        const imageTakenTime = selectedItem.imageTakenTime
        return getString(watermark.waterMarkTemplate, imageTakenTime)
    }

    private getDrawContext(): DrawContext {

        return {
            ctx: this.ctx,
            canvas: this.refCanvas.current,
            dpr: window.devicePixelRatio,
            width: this.state.width,
            height: this.state.height,
            ratio: this.ratio,
            isCollect: false,
            fileInfo: this.props.selectedItem,
        }
    }

    private getWaterMarkWH(waterMarkStr: string, watermark: Watermark) {
        const {
            fontName,
            fontSize,
            isBold,
            isItalic,
        } = watermark
        return getTextWH(waterMarkStr, { fontFamily: fontName, fontSize, isBold, isItalic })
    }

    private updateSize = () => {
        const container = this.refContainer.current;
        const selectedItem = this.props.selectedItem;
        if (!container || !selectedItem) return;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        const image = selectedItem.image;
        const originWidth = image.width;
        const originHeight = image.height;
        this.ratio = getImageRatio(originWidth, originHeight, containerWidth, containerHeight);
        const adaptiveWidth = this.ratio * originWidth;
        const adaptiveHeight = this.ratio * originHeight;
        const left = (containerWidth - adaptiveWidth) / 2;
        const top = (containerHeight - adaptiveHeight) / 2;
        this.setState({ left, top, width: adaptiveWidth, height: adaptiveHeight })
        this.props.setImageEditViewWH({ width: containerWidth, height: containerHeight })
    }

    private getSelectedRefWatermark(watermarkId: string | null | undefined) {
        if (!watermarkId) {
            return null;
        }
        return this.state.refWaterMarksMap.get(watermarkId);
    }

    private handleMouseDown = (event: React.MouseEvent | React.TouchEvent) => {
        // 在水印文字上mousdown,不能让outline消失
        this.mouseDownWatermarkFlag = true
        this.startMoveFlag = true

        const target = event.target as HTMLElement;
        const watermarkId = target.getAttribute('data-watermark-id');
        if (!watermarkId) {
            return;
        }

        this.props.setSelectedWatermarkId(watermarkId);
        const selectedRefWatermark = this.getSelectedRefWatermark(watermarkId);
        if (!selectedRefWatermark) {
            return;
        }

        if (!this.refWaterMarkContainer.current) {
            return;
        }
        this.waterMarkContainerRect = this.refWaterMarkContainer.current.getBoundingClientRect()

        const waterMark = selectedRefWatermark.current
        if (!waterMark) {
            return;
        }

        const watermark = this.props.watermarks.find((watermark) => watermark.id === watermarkId)
        if (!watermark) {
            return;
        }
        // 获取旋转后，水印的4个顶点的位置(相对于浏览器)
        const waterMarkCorners = getRotatedElementCorners(waterMark)
        // 获取旋转前，水印的4个顶点的坐标(相对于浏览器)
        const newRotatedRectCorners = getRotatedRectCorners(waterMarkCorners, -watermark.angel)
        // 旋转前，水印左上角的坐标(相对于浏览器)
        const waterMarkLeft = newRotatedRectCorners[0].x
        const waterMarkTop = newRotatedRectCorners[0].y
        const clientX = event.type.startsWith('mouse') ? (event as React.MouseEvent).clientX : ((event as React.TouchEvent).touches[0]).clientX;
        const clientY = event.type.startsWith('mouse') ? (event as React.MouseEvent).clientY : ((event as React.TouchEvent).touches[0]).clientY;
        this.xOffsetWaterMark = clientX - waterMarkLeft
        this.yOffsetWaterMark = clientY - waterMarkTop
    }

    private handleWaterMarkContainerMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        // 在水印文字外面mousedown，要让outline消失
        if (!this.mouseDownWatermarkFlag && this.props.position.positionType !== PositionType.Flat) {
            this.props.setSelectedWatermarkId(null)
            this.props.setImageEditViewStatus(EditViewStatus.Default)
        }
        this.mouseDownWatermarkFlag = false
    }

    private handleRotateAnchorDotMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        const selectedRefWatermark = this.getSelectedRefWatermark(this.props.selectedWatermarkId);
        if (!selectedRefWatermark || !selectedRefWatermark.current) return;
        const waterMarkRect = selectedRefWatermark.current.getBoundingClientRect()
        const { left, top, width, height, bottom, right } = waterMarkRect

        this.startRotateProps = {
            waterMarkRect: { left, top, width, height, bottom, right },
        }
        this.setState({ cursorStyle: 'crosshair' })
        event.stopPropagation()
    }

    /*
    如果不加throttle, CustomLeftTop的 antd Slider组件会一直更新，导致控制台出现如下警告：
    Warning: Maximum update depth exceeded. This can happen when a component calls setState inside useEffect, but useEffect either doesn't have a dependency array, or one of the dependencies changes on every render. 
    */

    private handleMouseMove = throttle((event: MouseEvent | TouchEvent) => {
        if (this.startMoveFlag) {
            this.moveWaterMark(event);
            return;
        }

        if (this.startSetSizeProps) {
            this.adjustWaterMarkSize(event);
            return;
        }

        if (this.startRotateProps) {
            this.rotateWaterMark(event);
            return;
        }
    }, 25)

    private moveWaterMark = (event: MouseEvent | TouchEvent) => {
        if (!this.waterMarkContainerRect || !this.waterMarkContainerRect) return;

        const selectedRefWatermark = this.getSelectedRefWatermark(this.props.selectedWatermarkId);
        const waterMark = selectedRefWatermark?.current;
        if (!waterMark) {
            return;
        }
        const { width, height } = this.state
        // 水印左上角的坐标(相对于图片)
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY
        const leftTopX = clientX - this.waterMarkContainerRect.left - this.xOffsetWaterMark
        const leftTopY = clientY - this.waterMarkContainerRect.top - this.yOffsetWaterMark
        let offsetX = leftTopX
        let offsetY = leftTopY
        // 水印平移后，以水印中心旋转前的坐标
        let rightX = leftTopX + waterMark.clientWidth
        let bottomY = leftTopY + waterMark.clientHeight
        const position = this.props.position
        switch (position.positionType) {
            case PositionType.LeftTop:
                this.props.setPosition({ ...position, offsetX, offsetY })
                break;

            case PositionType.RightTop:
                offsetX = width - rightX
                this.props.setPosition({ ...position, offsetX, offsetY })
                break;

            case PositionType.RightBottom:
                offsetX = width - rightX
                offsetY = height - bottomY
                this.props.setPosition({ ...position, offsetX, offsetY })
                break;

            case PositionType.LeftBottom:
                offsetY = height - bottomY
                this.props.setPosition({ ...position, offsetX, offsetY })
                break;

            case PositionType.Center:
                // 如果在中心位置模式调整位置，则切换到左上角模式
                this.props.setPosition({ ...position, positionType: PositionType.LeftTop, offsetX, offsetY })
                break;
            case PositionType.Top:
                // 水位位置不能移动
                this.props.setPosition({ ...position, offsetY })
                break;
            case PositionType.Bottom:
                // 水位位置不能移动
                offsetY = height - bottomY
                this.props.setPosition({ ...position, offsetY })
                break;
            default:

        }


    }

    private adjustWaterMarkSize = (event: MouseEvent | TouchEvent) => {
        if (!this.startSetSizeProps || !this.waterMarkContainerRect) return;
        const { fontName, isBold, isItalic, updateSelectedWatermark } = this.props;
        const selectedWatermark = this.props.getSelectedWatermark()
        if (!selectedWatermark) return
        const waterMarkStr = this.getWaterMarkStr(selectedWatermark)
        // 获取缩放前，旋转后元素的4个顶点的位置和宽高
        const { anchorDotType, fontSize, rotatedElementCorners, offsetWidth, offsetHeight } = this.startSetSizeProps;
        const originDistance = Math.sqrt(offsetWidth * offsetWidth + offsetHeight * offsetHeight);
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

        // 获取缩放前，对角点的位置
        const { diagonalAnchorX, diagonalAnchorY } = this.getAnchorDiagonal(anchorDotType, rotatedElementCorners);
        const distance = Math.sqrt((clientX - diagonalAnchorX) * (clientX - diagonalAnchorX) + (clientY - diagonalAnchorY) * (clientY - diagonalAnchorY));
        // 获取原始缩放比
        const originRatio = distance / originDistance;
        // 获取新的字体大小，可能为小数
        const newFontSize = fontSize * originRatio;
        // 字体大小取整
        const newFontSizeNum = Math.round(newFontSize)
        // 得到最终水印的宽高
        const { width: newWaterMarkWidth, height: newWaterMarkHeight } = getTextWH(waterMarkStr, { fontFamily: fontName, fontSize: newFontSizeNum, isBold, isItalic })
        // 获取最终缩放比
        const zoomRatio = newWaterMarkWidth / offsetWidth;

        // 计算缩放前对角点之外的其余三个点的位置(相对于浏览器)
        const anchorDots = this.getAnchorDotsExceptDiagonal(anchorDotType, rotatedElementCorners);
        // 计算缩放后，对角点之外的其余三个点的位置(相对于浏览器)
        const newAnchorDots = anchorDots.map((dot) => {
            const newDotX = (dot.x - diagonalAnchorX) * zoomRatio + diagonalAnchorX
            const newDotY = (dot.y - diagonalAnchorY) * zoomRatio + diagonalAnchorY
            return { x: newDotX, y: newDotY }
        })
        const newAllAnchorDots = [{ x: diagonalAnchorX, y: diagonalAnchorY }, ...newAnchorDots]
        // 对缩放后的4个顶点进行反旋转操作，得到旋转前的4个顶点的坐标
        const newRotatedRectCorners = getRotatedRectCorners(newAllAnchorDots, -this.props.angel)

        // 获取newRotatedRectCorners数组中最小的x
        const leftX = Math.min(...newRotatedRectCorners.map((corner) => corner.x)) - this.waterMarkContainerRect.left
        // 获取newRotatedRectCorners数组中最小的y
        const topY = Math.min(...newRotatedRectCorners.map((corner) => corner.y)) - this.waterMarkContainerRect.top
        let offsetX = leftX
        let offsetY = topY
        let rightX = leftX + newWaterMarkWidth
        let bottomY = topY + newWaterMarkHeight
        const position = this.props.position
        const { width, height } = this.state

        switch (position.positionType) {
            case PositionType.LeftTop:
                break;

            case PositionType.RightTop:
                offsetX = width - rightX
                break;

            case PositionType.RightBottom:
                offsetX = width - rightX
                offsetY = height - bottomY
                break;

            case PositionType.LeftBottom:
                offsetY = height - bottomY
                break;
            default:
        }
        updateSelectedWatermark({ fontSize: newFontSizeNum, position: { ...position, offsetX, offsetY } })
    }

    private rotateWaterMark(event: MouseEvent | TouchEvent) {
        if (!this.waterMarkContainerRect || !this.startRotateProps) return;
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
        const waterMarkRect = this.startRotateProps.waterMarkRect
        const waterMarkCenterX = waterMarkRect.left + waterMarkRect.width / 2
        const waterMarkCenterY = waterMarkRect.top + waterMarkRect.height / 2

        // tempAngel为向量与-y方向的夹角
        const tempAngel = Math.atan2(clientX - waterMarkCenterX, clientY - waterMarkCenterY) * 180 / Math.PI
        const angel = tempAngel < 0 ? -180 - tempAngel : 180 - tempAngel
        this.props.setAngel(Math.round(angel))
    }

    private handleMouseUp = (event: MouseEvent | TouchEvent) => {
        this.startMoveFlag = false;
        this.startSetSizeProps = null;
        this.startRotateProps = null;
        this.setState({ cursorStyle: undefined })
    }

    private getAnchorDiagonal(anchorDotType: AnchorDotType, rotatedElementCorners: any[]): { diagonalAnchorX: number, diagonalAnchorY: number } {
        let diagonalDotType = AnchorDotType.LeftTop;
        switch (anchorDotType) {
            case AnchorDotType.LeftTop:
                diagonalDotType = AnchorDotType.RightBottom
                break;
            case AnchorDotType.RightTop:
                diagonalDotType = AnchorDotType.LeftBottom
                break;
            case AnchorDotType.RightBottom:
                diagonalDotType = AnchorDotType.LeftTop
                break;
            case AnchorDotType.LeftBottom:
                diagonalDotType = AnchorDotType.RightTop
                break;
        }
        const diagonalAnchorX = rotatedElementCorners[diagonalDotType].x;
        const diagonalAnchorY = rotatedElementCorners[diagonalDotType].y;
        return { diagonalAnchorX, diagonalAnchorY };
    }

    // 获取旋转后，获取除当前锚点的对角点外的其他三个锚点类型
    private getAnchorDotTypesExceptDiagonal(anchorDotType: AnchorDotType): AnchorDotType[] {
        const before = mod(anchorDotType - 1, 4)
        const after = mod(anchorDotType + 1, 4)
        return [before, anchorDotType, after]
    }

    // 获取旋转后，获取除当前锚点的对角点外的其他三个锚点坐标
    private getAnchorDotsExceptDiagonal(anchorDotType: AnchorDotType, rotatedElementCorners: any[]): IPoint[] {
        const anchorDotTypesExceptDiagonal = this.getAnchorDotTypesExceptDiagonal(anchorDotType);
        const anchorDots = []
        for (let i = 0; i < anchorDotTypesExceptDiagonal.length; i++) {
            const anchorDotType = anchorDotTypesExceptDiagonal[i]
            anchorDots.push(rotatedElementCorners[anchorDotType])
        }
        return anchorDots;
    }

    private handleAnchorDotSelect = (anchorDotType: AnchorDotType) => {
        const selectedRefWatermark = this.getSelectedRefWatermark(this.props.selectedWatermarkId);
        const waterMark = selectedRefWatermark?.current;
        if (!waterMark || !this.refWaterMarkContainer.current) return;
        // 获取矩形原始宽高，因为矩形旋转后，getBoundingClientRect为包围盒的矩形
        const offsetWidth = waterMark.offsetWidth;
        const offsetHeight = waterMark.offsetHeight;
        const waterMarkRect = waterMark.getBoundingClientRect()
        const { left, top, width, height, bottom, right } = waterMarkRect

        const rotatedElementCorners = getRotatedElementCorners(waterMark)

        this.waterMarkContainerRect = this.refWaterMarkContainer.current.getBoundingClientRect()

        this.startSetSizeProps = {
            fontSize: this.props.fontSize,
            anchorDotType,
            waterMarkRect: { left, top, width, height, bottom, right },
            offsetWidth,
            offsetHeight,
            rotatedElementCorners,
        }
        this.setState({ cursorStyle: getAnchorDotInfo(anchorDotType, this.AnchorDotSize).cursor })
    }

    public render() {
        const { selectedItem } = this.props
        if (!selectedItem) {
            return null
        }
        const { left, top, width, height, cursorStyle } = this.state

        const dpr = window.devicePixelRatio
        const eventProps = userAgentUtils.isMobile ? {
            onTouchStart: this.handleWaterMarkContainerMouseDown,
        } : {
            onMouseDown: this.handleWaterMarkContainerMouseDown,
        }
        return (
            <div className='image-edit-view' style={{ cursor: cursorStyle }} ref={this.refContainer}>
                <img className="image-edit-img2" style={{ left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` }} src={selectedItem?.fileDataUrl} alt={selectedItem.file.name} />
                <div ref={this.refWaterMarkContainer} className='image-real-size' {...eventProps} style={{ left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` }}>
                    {this.renderWaterMarks()}
                    <canvas ref={this.refCanvas} className='image-edit-canvas' width={width * dpr} height={height * dpr} />
                </div>
            </div>
        )
    }

    private renderWaterMarks() {
        const { watermarks } = this.props
        return watermarks.map((watermark) => {
            return this.renderWaterMark(watermark)
        })
    }

    private renderWaterMark(watermark: Watermark) {
        const { selectedWatermarkId } = this.props
        const {
            id,
            fontName,
            fontSize,
            fontColor,
            angel,
            tran,
            position,
            isBold,
            isItalic,
            isUnderline,
            isStrikethrough,
            stroke,
        } = watermark

        const isCurrentSelected = selectedWatermarkId === id

        const waterMarkStr = this.getWaterMarkStr(watermark)
        const { width, height, cursorStyle } = this.state
        const { positionType, offsetX, offsetY } = position

        const curFontSize = fontSize
        const { width: watermarkWidth, height: watermarkHeight } = this.getWaterMarkWH(waterMarkStr, watermark)
        const opacity = 1 - tran / 100

        const positionStyle: any = {}
        switch (positionType) {
            case PositionType.LeftTop:
                positionStyle.left = `${offsetX}px`
                positionStyle.top = `${offsetY}px`
                break;
            case PositionType.RightTop:
                positionStyle.right = `${offsetX}px`
                positionStyle.top = `${offsetY}px`
                break;
            case PositionType.RightBottom:
                positionStyle.right = `${offsetX}px`
                positionStyle.bottom = `${offsetY}px`
                break;
            case PositionType.LeftBottom:
                positionStyle.left = `${offsetX}px`
                positionStyle.bottom = `${offsetY}px`
                break;
            default:
                const { offsetLeft, offsetTop } = getPositionLeftTop({ position, watermarkWidth, watermarkHeight, width, height, isCollect: false, ratio: 1 })
                positionStyle.left = `${offsetLeft}px`
                positionStyle.top = `${offsetTop}px`
        }

        const eventProps = userAgentUtils.isMobile ? {
            onTouchStart: this.handleMouseDown,
        } : {
            onMouseDown: this.handleMouseDown,
        }

        if (positionType !== PositionType.Flat) {
            return (
                <div ref={this.state.refWaterMarksMap.get(id)}
                    className='water-mark'
                    key={watermark.id}
                    data-watermark-id={watermark.id}
                    style={
                        {
                            color: fontColor,
                            WebkitTextStroke: stroke ? `${stroke.strokeWidth}px ${stroke.strokeColor}` : undefined,
                            ...positionStyle,
                            width: `${watermarkWidth}px`,
                            height: `${watermarkHeight}px`,
                            fontSize: `${curFontSize}px`,
                            fontFamily: `"${fontName}" ,serif`,
                            fontWeight: isBold ? 'bold' : 'normal',
                            fontStyle: isItalic ? 'italic' : 'normal',
                            textDecoration: `${isUnderline ? 'underline' : ''} ${isStrikethrough ? 'line-through' : ''}`,
                            lineHeight: `${watermarkHeight}px`,
                            transform: `rotate(${angel}deg)`,
                            opacity,
                            cursor: cursorStyle ? cursorStyle : 'move',
                            outline: isCurrentSelected ? '1px solid black' : undefined,
                        }
                    }
                    {...eventProps}
                >
                    {isCurrentSelected && <>
                        <AnchorDot anchorDotType={AnchorDotType.LeftTop} size={this.AnchorDotSize} onSelect={this.handleAnchorDotSelect} />
                        <AnchorDot anchorDotType={AnchorDotType.RightTop} size={this.AnchorDotSize} onSelect={this.handleAnchorDotSelect} />
                        <AnchorDot anchorDotType={AnchorDotType.LeftBottom} size={this.AnchorDotSize} onSelect={this.handleAnchorDotSelect} />
                        <AnchorDot anchorDotType={AnchorDotType.RightBottom} size={this.AnchorDotSize} onSelect={this.handleAnchorDotSelect} />
                        <RotateAnchorDot size={this.AnchorDotSize} lineLen={50} lineWidth={2} onSelect={this.handleRotateAnchorDotMouseDown} />
                    </>}
                    {functionSwitchConfig.enableDivWatermark && waterMarkStr}
                </div>
            )
        }

        if (!functionSwitchConfig.enableDivWatermark) {
            return null
        }

        const list = []
        const start = curFontSize
        const distanceX = curFontSize * 2 + watermarkWidth
        const distanceY = curFontSize * 2 + watermarkHeight
        let i = 0;
        while (start + i * distanceX < width) {
            let j = 0;
            while (start + j * distanceY < height) {
                const curWaterMarkLeft = start + i * distanceX
                const curWaterMarkTop = start + j * distanceY
                list.push(
                    <div key={`${i}-${j}`} className='water-mark' style={
                        {
                            color: fontColor.toRgbaString(),
                            WebkitTextStroke: stroke ? `${stroke.strokeWidth}px ${stroke.strokeColor}` : undefined,
                            left: `${curWaterMarkLeft}px`,
                            top: `${curWaterMarkTop}px`,
                            fontSize: `${curFontSize}px`,
                            fontFamily: `"${fontName}" ,serif`,
                            fontWeight: isBold ? 'bold' : 'normal',
                            fontStyle: isItalic ? 'italic' : 'normal',
                            lineHeight: `${watermarkHeight}px`,
                            transform: `rotate(${angel}deg)`,
                            opacity,
                        }
                    }>{waterMarkStr}</div>
                );
                j++
            }
            i++
        }
        return list
    }
}

export default ImageEditView